import React from "react";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import { Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import Typography from "@limitless/modern/data-display/typography/Typography";
import { useTranslation } from "@limitless/common/hooks";
import Chip from "@limitless/modern/data-display/chip/Chip";
import { useExpertOnboardingContext } from "@limitless/expert";

import { useNavigation } from "@limitless/portal";
import { recordGroupName } from "@limitless/analytics";

import ArrowRight from "@limitless/icons/ArrowRight";
import ExpertDocumentVerificationOverview from "./ExpertDocumentVerificationOverview";
import {
    extraInfoDetails,
    getMemberDetailsStatus,
    getPracticeQuestionStatus,
    isDocumentVerificationRequired,
    isRedChip,
    shouldPracticeTaskVisible
} from "@limitless/expert/utils/OnboardingStatsHelper";

const useStyles = makeStyles(theme => ({
    forwardIcon: {
        fontSize: "1rem",
        marginLeft: 8,
        marginBottom: 1
    },
    cta: {
        marginLeft: "auto"
    },
    row1Text: {
        color: theme.palette.grey[4]
    },
    row2Text: {
        display: "flex",
        alignItems: "center",
        color: theme.palette.primary.main
    },
    chip: {
        backgroundColor: theme.palette.secondary.light,
        minWidth: 57,
        borderRadius: "8px",
        marginLeft: theme.spacing(1),
        whiteSpace: "nowrap"
    },
    chipRed: {
        backgroundColor: theme.palette.error.light,
        minWidth: 57,
        borderRadius: "8px",
        marginLeft: theme.spacing(1),
        whiteSpace: "nowrap"
    },
    chipGray: {
        backgroundColor: theme.palette.grey[0.5],
        minWidth: 57,
        borderRadius: "8px",
        marginLeft: theme.spacing(1),
        whiteSpace: "nowrap"
    },
    labelBox: {
        display: "flex",
        alignItems: "center"
    },
    icon: {
        marginLeft: theme.spacing(2),
        color: theme.palette.primary.main,
        fontSize: "16px !important"
    },
    itemGrid: {
        display: "flex",
        justifyContent: "center",
        gap: 10,

        maxWidth: "100%"
    }
}));

const ExpertPracticeGroupCTA = ({
    practiceGroup,
    isIdentityVerified,
    isDocumentVerified,
    isPaypalVerified,
    isNotStartedTab = false
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const { onboardingDetail, getOnboardingDetail } = useExpertOnboardingContext();
    const { navigate } = useNavigation();

    // Added: Optimized CTAData configuration to reduce code duplication
    const baseCTAData = [
        {
            key: "practiceTask",
            label: "Practice Tasks",
            getValue: () => getPracticeQuestionStatus(practiceGroup, isNotStartedTab),
            getOriginalValue: () => getPracticeQuestionStatus(practiceGroup),
            getShouldVisible: () => shouldPracticeTaskVisible(practiceGroup),
            row: 1,
            sx: { color: theme => theme.palette.grey[4] },
            icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.grey[9] }} />
        },
        {
            key: "additionalDetails",
            label: "Additional Details",
            getValue: () => getMemberDetailsStatus(practiceGroup, isNotStartedTab),
            getOriginalValue: () => getMemberDetailsStatus(practiceGroup),
            getShouldVisible: () => !!extraInfoDetails(practiceGroup),
            sx: { ...(shouldPracticeTaskVisible(practiceGroup) && { ml: 2 }) },
            row: 1,
            icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.grey[9] }} />
        },
        // Commented out as part of ticket LD-15156: Hide ID Verification / Document Verification
        // from all onboarding screens,summary, Detail tab, and Progress card.

        // {
        //     key: "idVerification",
        //     label: "ID Verification",
        //     getValue: () => (!isIdentityVerified ? "to do" : "n/a"),
        //     getOriginalValue: () => `${!isIdentityVerified && "to do"}`,
        //     getShouldVisible: () => !isIdentityVerified,
        //     icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />,
        //     navigateTo: "/onboarding/detail?tab=idVerificationTab",
        //     row: 2
        // },
        // {
        //     key: "documentVerification",
        //     label: "Document Verification",
        //     getValue: () => (isDocumentVerificationRequired(practiceGroup, isDocumentVerified) ? "to do" : "n/a"),
        //     getOriginalValue: () => `${isDocumentVerificationRequired(practiceGroup, isDocumentVerified) && "to do"}`,
        //     getShouldVisible: () => isDocumentVerificationRequired(practiceGroup, isDocumentVerified),
        //     icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />,
        //     sx: { ml: 2 },
        //     component: <ExpertDocumentVerificationOverview onboardingDetail={onboardingDetail} />,
        //     navigateTo: "/onboarding/detail?tab=documentVerificationTab",
        //     row: 2
        // },
        {
            key: "paymentDetails",
            label: "Payment Details",
            getValue: () => (!isPaypalVerified ? "to do" : "complete"),
            getOriginalValue: () => (!isPaypalVerified ? "to do" : "complete"),
            getShouldVisible: () => true,
            icon: <ArrowRight fontSize="small" sx={{ color: theme => theme.palette.primary.main }} />,
            sx: { ml: 2 },
            navigateTo: "/settings?tab=payment",
            row: 2
        }
    ];

    const CTAData = baseCTAData.map(item => {
        const baseConfig = {
            label: isNotStartedTab && item.key === "practiceTask" ? `${item.label}` : item.label,
            value: isNotStartedTab ? (item.getShouldVisible() ? item.getValue() : "n/a") : item.getOriginalValue(),
            shouldVisible: isNotStartedTab ? true : item.getShouldVisible(),
            row: item.row,
            ...(item.sx && { sx: item.sx }),
            ...(item.icon && { icon: item.icon }),
            ...(item.navigateTo && { navigateTo: item.navigateTo }),
            ...(item.component && { component: item.component })
        };

        return baseConfig;
    });

    return (
        <Box>
            {/* Row 1: Practice Tasks and Additional Details - aligned with first 2 items of row 2 */}
            <Grid container alignItems="center" pt={1}>
                {CTAData.filter(item => item.row === 1).map(
                    (item, index) =>
                        item.shouldVisible && (
                            <Grid item xs={4} key={index} container alignItems="center" spacing={1} sx={item.sx}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" className={classes.row1Text}>
                                        <Box className={classes.labelBox}>{item.label}</Box>
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "center" }}>
                                    {item.icon &&
                                        React.cloneElement(item.icon, {
                                            sx: {
                                                ml: 2,
                                                fontSize: "16px"
                                            }
                                        })}
                                </Grid>
                                <Grid item xs={4}>
                                    <Chip
                                        className={
                                            item?.value === "n/a"
                                                ? classes.chipGray
                                                : isRedChip(item?.value)
                                                ? classes.chip
                                                : classes.chip
                                        }
                                        label={t(item?.value) || ""}
                                    />
                                </Grid>
                            </Grid>
                        )
                )}

                <Grid item xs={4}></Grid>
            </Grid>
            {/* Row 2: ID Verification, Document Verification, Payment Details - aligned columns */}
            <Grid container alignItems="center" pt={1}>
                {CTAData.filter(item => item.row === 2).map(
                    (item, index) =>
                        item.shouldVisible && (
                            <Grid
                                item
                                xs={4}
                                key={index}
                                container
                                alignItems="center"
                                spacing={1}
                                onClick={() => {
                                    if (item.navigateTo) {
                                        getOnboardingDetail(practiceGroup);
                                        navigate(item.navigateTo);
                                        recordGroupName(practiceGroup.groupName);
                                    }
                                }}
                                sx={{ cursor: item.navigateTo ? "pointer" : "default" }}
                            >
                                <Grid item xs={6}>
                                    <Typography variant="body2" className={classes.row2Text}>
                                        <Box className={classes.labelBox}>{item.label}</Box>
                                    </Typography>
                                </Grid>
                                <Grid item xs={2} sx={{ textAlign: "center" }}>
                                    {item.icon &&
                                        React.cloneElement(item.icon, {
                                            className: classes.icon
                                        })}
                                </Grid>
                                <Grid item xs={4}>
                                    <Chip
                                        className={item?.value === "n/a" ? classes.chipGray : classes.chip}
                                        label={item.value}
                                    />
                                </Grid>
                            </Grid>
                        )
                )}
            </Grid>
        </Box>
    );
};

export default ExpertPracticeGroupCTA;

ExpertPracticeGroupCTA.propTypes = {
    practiceGroup: PropTypes.object.isRequired,
    type: PropTypes.string,
    isIdentityVerified: PropTypes.bool,
    isDocumentVerified: PropTypes.bool,
    isPaypalVerified: PropTypes.string,
    index: PropTypes.number.isRequired,
    isNotStartedTab: PropTypes.bool
};
